<template>
  <component
    :is="tag"
    v-bind="attrs"
    class="pill-btn"
    :class="[`is-${variant}`, `is-${size}`]"
  >
    <slot />
  </component>
</template>

<script setup>
import { computed, resolveComponent } from 'vue'
import { useLocale } from '../composables/useLocale'

const props = defineProps({
  // App path without a locale prefix, e.g. "/contact".
  to: { type: String, default: '' },
  // External or mailto: link.
  href: { type: String, default: '' },
  // primary | secondary | outline | white | dark
  variant: { type: String, default: 'primary' },
  // lg | md | sm
  size: { type: String, default: 'lg' },
  type: { type: String, default: 'button' }
})

const { to: localised } = useLocale()

const tag = computed(() => {
  if (props.to) {
    return resolveComponent('RouterLink')
  }
  return props.href ? 'a' : 'button'
})

const attrs = computed(() => {
  if (props.to) {
    return { to: localised(props.to) }
  }
  return props.href ? { href: props.href } : { type: props.type }
})
</script>

<style scoped>
.pill-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1.5px solid transparent;
  border-radius: var(--radius-pill);
  font-family: var(--font-body);
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

.is-lg {
  padding: 18px 30px;
  font-size: 17px;
}

.is-md {
  padding: 14px 24px;
  font-size: 16px;
}

.is-sm {
  padding: 12px 20px;
  font-size: 15px;
}

.is-primary {
  background: var(--primary);
  color: var(--ink);
}

.is-primary:hover {
  background: var(--primary-hover);
  color: var(--ink);
}

.is-secondary {
  background: var(--surface);
  color: var(--ink);
}

.is-secondary:hover {
  background: var(--divider);
  color: var(--ink);
}

.is-outline {
  background: transparent;
  border-color: var(--ink);
  color: var(--ink);
}

.is-outline:hover {
  background: var(--ink);
  color: #fff;
}

.is-white {
  background: #fff;
  color: var(--ink);
}

.is-white:hover {
  background: var(--sand);
  color: var(--ink);
}

.is-dark {
  background: var(--ink);
  color: #fff;
}

.is-dark:hover {
  background: #000;
  color: #fff;
}
</style>
